import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Scale } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [loading, setLoading] = useState(false);
  const isRTL = language === 'ar' || language === 'ur';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    nationality: '',
    privacy_consent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Login effettuato con successo!');
        navigate('/dashboard');
      } else {
        if (!formData.privacy_consent) {
          toast.error(t('auth.privacyConsent'));
          setLoading(false);
          return;
        }
        await register(formData);
        toast.success('Registrazione completata!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'autenticazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
          backgroundImage: `url('https://images.pexels.com/photos/6077797/pexels-photo-6077797.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/30 to-primary/90" />
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <img 
            src="https://customer-assets.emergentagent.com/job_legal-booking-hub/artifacts/evfhhmo1_logo%20studio%20legale_edited_edited.avif" 
            alt="Studio Legale"
            className="h-40 w-40 object-contain mb-8"
            style={{ imageRendering: 'crisp-edges', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
          />
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Studio Legale Nada Zhiti
          </h1>
          <p className="text-xl text-center text-gray-200">
            Consulenza legale professionale
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 sm:p-12 lg:p-16 bg-white">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {isLogin ? t('auth.login') : t('auth.register')}
            </h2>
            <p className="text-muted-foreground">
              {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}{' '}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-secondary hover:underline font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? t('auth.register') : t('auth.login')}
              </button>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">{t('auth.firstName')}</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">{t('auth.lastName')}</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="input-last-name"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="input-email"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="input-password"
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="phone">{t('auth.phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="nationality">{t('auth.nationality')}</Label>
                  <Input
                    id="nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    required
                    data-testid="input-nationality"
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="privacy_consent"
                    name="privacy_consent"
                    checked={formData.privacy_consent}
                    onCheckedChange={(checked) => handleChange({ target: { name: 'privacy_consent', type: 'checkbox', checked } })}
                    data-testid="checkbox-privacy"
                    className="mt-1"
                  />
                  <Label htmlFor="privacy_consent" className="text-sm cursor-pointer leading-tight">
                    {t('auth.privacyConsent')}
                  </Label>
                </div>
              </>
            )}

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 rounded-sm py-6"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? '...' : isLogin ? t('auth.loginButton') : t('auth.registerButton')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
